/**
 * **A website the record carries, as something a reader can click** (owner
 * ruling, 2026-09-10: "the website link should be clickable, opening a new
 * tab").
 *
 * The record prints an organisation's site in two places — the line under the
 * name and the About block's Website row — and both used to be plain text. That
 * was a deliberate choice at the time, on the grounds that nothing else on a
 * record page leaves the app; the owner has since asked for the opposite, and
 * this is that, in one place so the two surfaces cannot spell it differently.
 *
 * **This reads a value for display, and decides nothing about identity.** The
 * authority on what a website *is* here is the server's `websiteHost`
 * (`server/src/modules/identity/website-host.ts`), which is what a confirmed
 * `domain` anchor is written through; the confirmed host is what both surfaces
 * are handed now (`organisation-record.service.ts`), so in the ordinary case
 * this function is handed a bare host and hands it straight back. The parsing
 * below exists for the other case: a profile on a record with no confirmed site
 * carries whatever a page printed, which may be a whole URL.
 *
 * The rules are the server's, for the same reasons: the host and nothing else,
 * `www.` dropped as the convention it is, a host with no dot or with whitespace
 * in it is not a website but a sentence somebody typed. A value this cannot
 * read comes back null, and the caller prints the text it was given without a
 * link — an address nobody can resolve is still what the source said.
 */
export interface WebsiteLink {
  /** What the reader sees: the host, lowercased, without `www.`. */
  readonly host: string;
  /** Where the link goes. Always `https`, never the scheme the value carried. */
  readonly href: string;
}

export function websiteLink(value: string | null | undefined): WebsiteLink | null {
  const typed = value?.trim() ?? '';
  if (!typed) return null;

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(typed) ? typed : `https://${typed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  /* Credentials mean somebody typed an email address into a website field, which
     is the server's own reading of this shape and not a rule invented here. */
  if (parsed.username || parsed.password) return null;

  const host = parsed.hostname
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/^www\./, '');
  if (!host.includes('.')) return null;
  if (/[\s%]/.test(host)) return null;
  return { host, href: `https://${host}` };
}
